import { Button } from "@common";
import { useIsInlineRowEditing, useInlineSavingRowKey } from "@hooks/useInlineEditStore";
import { toDraftValue } from "@utils/inlineEditDraft";

/**
 * One cell in an inline-editable row.
 *
 * When the row is not in edit mode it falls through to the column's
 * normal `renderStaticCell` to keep the static table identical to the
 * non-editing case.
 *
 * The input is uncontrolled (`defaultValue` + `onChange` writing to the
 * external store) so keystrokes never trigger a React re-render of the
 * surrounding table.
 */
export const InlineEditableCell = ({
  row,
  rowIndex,
  rowKey,
  columnKey,
  store,
  onDraftChange,
  renderStaticCell,
}) => {
  const isEditing = useIsInlineRowEditing(store, rowKey);

  if (!isEditing) return renderStaticCell(row, rowIndex);

  const isFeeField = columnKey === "associate_fee_percentage";
  const isHoursField = columnKey === "hours_worked";
  const inputDefault = store.getDraftValue(rowKey, columnKey) ?? toDraftValue(row[columnKey]);

  return (
    <input
      key={`${rowKey}-${columnKey}`}
      type="number"
      inputMode="decimal"
      min={0}
      max={isFeeField ? 100 : undefined}
      step={isHoursField ? "0.25" : "0.01"}
      defaultValue={inputDefault}
      onChange={(event) => onDraftChange(rowKey, columnKey, event.target.value)}
      className="h-8 w-full min-w-[86px] rounded border border-palette-indigo-border px-2 text-xs text-palette-slate-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
    />
  );
};

/**
 * Action column for the inline-editable row.
 *
 * Renders Edit / Save+Cancel based on the store's editing state, and
 * gates editability behind the page-supplied permission flags.
 *
 * NOTE: This still encodes Associate-Fee specific business rules
 * (`practitioner_id` presence, `approval` lock). If you ever need to
 * reuse it on other tables, lift those checks out into a `canEdit(row)`
 * prop so the cell becomes domain-agnostic.
 */
export const InlineEditActionCell = ({
  row,
  rowKey,
  store,
  canEditPractitioner,
  isAdminOrOrg,
  onStartEdit,
  onSave,
  onCancel,
}) => {
  const isEditing = useIsInlineRowEditing(store, rowKey);
  const savingRowKey = useInlineSavingRowKey(store);
  const isSaving = savingRowKey === rowKey;
  const adminApproval = row.admin_approval;
  const isAdminApproved =
    !!adminApproval && typeof adminApproval === "object" && adminApproval.approved === true;
  const approvalLocked = isAdminApproved && !isAdminOrOrg;
  const cannotEdit = !canEditPractitioner || !row.practitioner_id || approvalLocked;

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Button variant="primary" size="xs" onClick={() => onSave(row)} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <Button variant="gray" size="xs" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    );
  }

  const disabled = cannotEdit || !!savingRowKey;
  let title = "Edit this row";
  if (!canEditPractitioner) title = "You do not have permission to edit.";
  else if (!row.practitioner_id) title = "Cannot edit this row.";
  else if (approvalLocked) title = "Approved rows can only be edited by admin/organizer.";

  return (
    <Button
      variant="outline"
      size="xs"
      onClick={() => onStartEdit(row)}
      disabled={disabled}
      title={title}
    >
      Edit
    </Button>
  );
};
